import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaMusic } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const MusicTag = ({ music }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
      }
    };
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!music.audio_url) return;

    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      audioRef.current.src = music.audio_url;
      audioRef.current.play().catch(() => {});
      setPlaying(true);
    }
  };

  const goToSongPosts = (e) => {
    e.stopPropagation();
    navigate(`/music/${music.song_id}`);
  };

  const handleEnded = () => setPlaying(false);

  const handleTimeUpdate = () => {
    if (audioRef.current && progressRef.current) {
      const pct = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      progressRef.current.style.width = `${pct}%`;
    }
  };

  return (
    <div
      className="flex items-center space-x-2.5 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl px-3 py-2 mb-3 group hover:border-[#4A90E2]/30 transition-all cursor-pointer"
      onClick={goToSongPosts}
      data-testid={`music-tag-${music.song_id}`}
    >
      <audio ref={audioRef} onEnded={handleEnded} onTimeUpdate={handleTimeUpdate} />

      {/* Album art + play */}
      <div className="relative w-9 h-9 rounded-lg overflow-hidden flex-shrink-0">
        <img src={music.album_art} alt={music.song_title} className="w-full h-full object-cover" />
        {music.audio_url && (
          <button
            onClick={togglePlay}
            className={`absolute inset-0 bg-black/40 flex items-center justify-center ${playing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
            data-testid={`play-btn-${music.song_id}`}
          >
            {playing ? <FaPause className="text-white text-[10px]" /> : <FaPlay className="text-white text-[10px] ml-0.5" />}
          </button>
        )}
        {/* Progress bar */}
        {playing && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/30">
            <div ref={progressRef} className="h-full bg-[#4A90E2] transition-[width] duration-200" style={{ width: '0%' }} />
          </div>
        )}
      </div>

      {/* Song info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-1.5">
          <FaMusic className="text-[10px] text-[#4A90E2]" />
          <p className="text-sm font-medium text-gray-900 truncate">{music.song_title}</p>
        </div>
        <p className="text-xs text-gray-500 truncate">{music.artist}</p>
      </div>

      {/* GDME logo */}
      <img src="/gdme-logo.svg" alt="GDME" className="h-3.5 flex-shrink-0" />

      {/* Animated bars when playing */}
      {playing && (
        <div className="flex items-end space-x-[2px] h-3 flex-shrink-0" data-testid="music-playing-indicator">
          <div className="w-[3px] bg-[#4A90E2] rounded-full animate-bounce" style={{ height: '8px', animationDelay: '0ms', animationDuration: '400ms' }} />
          <div className="w-[3px] bg-[#4A90E2] rounded-full animate-bounce" style={{ height: '12px', animationDelay: '150ms', animationDuration: '400ms' }} />
          <div className="w-[3px] bg-[#4A90E2] rounded-full animate-bounce" style={{ height: '6px', animationDelay: '300ms', animationDuration: '400ms' }} />
        </div>
      )}
    </div>
  );
};

export default MusicTag;
